import {
  mdiInformation,
  mdiPencil,
  mdiPlay,
  mdiTriangleSmallDown,
} from "@mdi/js";
import { Button, Modal, Space, Tooltip, Typography } from "antd";
import {
  RoleListVm,
  SimpleUserForUserRoleRelationVm,
} from "api/generated/lumen";
import { useDispatch, useSelector } from "app/store";
import { MaterialIcon } from "components/MaterialIcon";
import CheckboxDisplay from "components/ui/CheckboxDisplay";
import useTable from "hooks/useTable";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "widgets/table/CustomTable";
import { ColumnStorageName } from "widgets/table/table-settings/ColumnStorageName";
import { ColumnConfigParams, FilterMode } from "widgets/table/useTableUtils";
import RoleSelect from "./RoleSelect/RoleSelect";
import styles from "./UsersAndRolesAssign.module.scss";
import { GetUserRoleRelationsRequestParams } from "./usersAndRolesAssign.store";
import clsx from "clsx";
import { useBlocker } from "react-router-dom";
import useNavigationBlocker from "widgets/form/hooks/useNavigationBlocker";

const { confirm } = Modal;

type TransformedRelationType = Record<
  string,
  Record<string, boolean | undefined>
>;

const UsersAndRolesAssign = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [isEditing, setEditing] = useState<boolean>(false);
  const [selectedRoles, setSelectedRoles] = useState<RoleListVm[]>([]);

  const { roles, relations, users, listParams, filterRoleId, isSaveDisabled } =
    useSelector((state) => state.usersAndRolesAssign);

  const { users: allUsers } = useSelector((state) => state.usersListSettings);

  const { getUserRoleRelations, updateUserRoleRelations } = useSelector(
    (state) => state.loading.effects.usersAndRolesAssign
  );

  useEffect(() => {
    dispatch.usersListSettings.fetchUsers({ pageSize: -1 });
  }, [dispatch.usersListSettings]);

  const changedValues = useRef<TransformedRelationType>({});

  const filteredRoles = useMemo(
    () =>
      [...((selectedRoles?.length ? selectedRoles : roles) ?? [])]?.sort(
        (a, b) => {
          if (a.isBuiltIn && !b.isBuiltIn) {
            return -1;
          } else if (!a.isBuiltIn && b.isBuiltIn) {
            return 1;
          }
          return a.name && b.name ? a.name?.localeCompare(b.name) : 0;
        }
      ),
    [roles, selectedRoles]
  );

  const transformedRelations = useMemo(
    () =>
      relations?.reduce<TransformedRelationType>((prev, { userId, roleId }) => {
        if (!userId || !roleId) {
          return prev;
        }

        if (!prev[userId]) {
          prev[userId] = { [roleId]: true };
        } else {
          prev[userId][roleId] = true;
        }

        return prev;
      }, {}),
    [relations]
  );

  const addOrRemoveRelation = useCallback(
    ({
      userId,
      roleId,
      checked,
    }: {
      userId: string;
      roleId: string;
      checked: boolean;
    }) => {
      if (checked === !!transformedRelations?.[userId]?.[roleId]) {
        delete changedValues.current[userId]?.[roleId];

        if (Object.keys(changedValues.current[userId]).length === 0) {
          delete changedValues.current[userId];
        }

        if (Object.keys(changedValues.current).length === 0) {
          dispatch.usersAndRolesAssign.setIsSaveDisabled(true);
        } else {
          dispatch.usersAndRolesAssign.setIsSaveDisabled(false);
        }

        return;
      }

      changedValues.current = {
        ...changedValues.current,
        [userId]: {
          ...(changedValues.current[userId] ?? {}),
          [roleId]: checked,
        },
      };

      if (Object.keys(changedValues.current).length !== 0) {
        dispatch.usersAndRolesAssign.setIsSaveDisabled(false);
      }
    },
    [dispatch.usersAndRolesAssign, transformedRelations]
  );

  const fetchTableData = useCallback(
    (requestParams?: GetUserRoleRelationsRequestParams) => {
      return dispatch.usersAndRolesAssign.getUserRoleRelations(requestParams);
    },
    [dispatch.usersAndRolesAssign]
  );

  const resetChangedValues = useCallback(() => {
    changedValues.current = {};
    fetchTableData(listParams);
    dispatch.usersAndRolesAssign.reset();
    dispatch.usersAndRolesAssign.setIsSaveDisabled(true);
  }, [dispatch.usersAndRolesAssign, fetchTableData, listParams]);

  const handleSaveRelations = useCallback(async () => {
    const relations = Object.entries(changedValues.current).flatMap(
      ([userId, roles]) =>
        Object.entries(roles).map(([roleId, relation]) => ({
          userId,
          roleId,
          relation,
        }))
    );

    if (relations) {
      confirm({
        okText: t("common.confirmDeleteModal.yes"),
        okType: "danger",
        cancelText: t("common.confirmDeleteModal.no"),
        title: t(
          "rolesAndPermissionsSettings.usersAndRolesAssign.confirmSaveModal.title"
        ),
        onOk: async () => {
          await dispatch.usersAndRolesAssign.updateUserRoleRelations({
            updateUserRoleRelationsCommand: { relations },
          });

          setEditing(false);
          resetChangedValues();
        },
        onCancel: () => {
          resetChangedValues();
          setEditing(false);
        },
      });
    }
  }, [dispatch.usersAndRolesAssign, resetChangedValues, t]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return (
      currentLocation.pathname !== nextLocation.pathname && !isSaveDisabled
    );
  });

  useNavigationBlocker({ blocker });

  const handleSortUsersWithRole = useCallback(
    async (role: RoleListVm) => {
      dispatch.usersAndRolesAssign.setFilterRoleId(role.id || "");
      fetchTableData(listParams);
    },
    [dispatch.usersAndRolesAssign, fetchTableData, listParams]
  );

  const handleSortRolesWithUser = useCallback(
    (userId: string) => {
      const filteredRoleIds = relations
        ?.filter((userRoleRelation) => userRoleRelation.userId === userId)
        .map(({ roleId }) => roleId);

      const filteredCheckedRoles =
        roles?.filter((role) => filteredRoleIds?.includes(role.id)) ?? [];

      setSelectedRoles(filteredCheckedRoles);
    },
    [roles, relations]
  );

  const splittedUsers = useMemo(() => {
    return users?.map((user) => ({
      ...user,
      name: user.name?.split("\n"),
    }));
  }, [users]);

  const columnParams = useMemo<
    ColumnConfigParams<SimpleUserForUserRoleRelationVm>[]
  >(() => {
    return [
      {
        key: "userIds",
        dataIndex: "id",
        title: t("common.userName"),
        fixed: "left",
        width: "16rem",
        filterMode: FilterMode.FILTER_SELECT,
        filters: (allUsers || []).map((item) => ({
          text: `${item.familyname} ${item.forename}`,
          value: item.id || "",
        })),

        render: (_: any, record) => {
          const user = splittedUsers?.find(
            (user) => user.id === record.id
          )?.name;
          return (
            <Space className={styles.userCellWrapper}>
              <Space direction="vertical">
                <Typography.Text className={styles.userText}>
                  {user?.at(0)}
                </Typography.Text>
                <Typography.Text className={styles.userText}>
                  {user?.at(1)}
                </Typography.Text>
              </Space>
              <Space>
                <div
                  onClick={() => handleSortRolesWithUser(record.id ?? "")}
                  className={styles.sortIcon}
                >
                  <MaterialIcon path={mdiPlay} size={0.75} />
                </div>
              </Space>
            </Space>
          );
        },
      },
      ...(filteredRoles ?? []).map((role) => {
        return {
          key: `userRole-${role.id}`,
          dataIndex: "id",
          width: "15rem",
          onHeaderCell: () => ({
            className: clsx({
              [styles.builtIn]: role.isBuiltIn,
            }),
          }),
          title: (
            <div className={styles.titleWrapper}>
              <Tooltip
                title={role.description}
                placement="topLeft"
                align={{ offset: [-15, 0] }}
              >
                <MaterialIcon path={mdiInformation} />
              </Tooltip>
              <div className={styles.title}>
                <Tooltip title={role.name} placement="topLeft">
                  {role.name}
                </Tooltip>
              </div>
              <div
                onClick={() => handleSortUsersWithRole(role)}
                className={styles.sortIcon}
              >
                <MaterialIcon path={mdiTriangleSmallDown} size={1.1} />
              </div>
            </div>
          ),

          render: (userId: string) => (
            <Space className={styles.roleCell}>
              <CheckboxDisplay
                isBuiltIn={role.isBuiltIn}
                disabled={!isEditing || role.isBuiltIn}
                isEditing={isEditing}
                value={
                  role.id
                    ? {
                        ...transformedRelations,
                        ...changedValues.current,
                      }[userId]?.[role.id] ?? false
                    : false
                }
                onChange={(checked) => {
                  if (!role.id) {
                    return;
                  }

                  addOrRemoveRelation({
                    userId,
                    roleId: role.id,
                    checked,
                  });
                }}
              />
            </Space>
          ),
        };
      }),
      {
        key: "emptyCol",
        fixed: "right",
        render: () => <></>,
      },
    ];
  }, [
    addOrRemoveRelation,
    filteredRoles,
    allUsers,
    handleSortRolesWithUser,
    handleSortUsersWithRole,
    isEditing,
    splittedUsers,
    t,
    transformedRelations,
  ]);

  const customTableProps = useTable({
    fetchTableData,
    columnParams,
    list: splittedUsers,
    listParams,
    columnStorageName: ColumnStorageName.USERS_AND_ROLES_ASSIGN,
    isResetCustomFilters: !!selectedRoles.length || !!filterRoleId,
    selectMode: "multiple",
    isFilterOption: false,
    roleSelect: (
      <RoleSelect
        selectedRoles={selectedRoles}
        handleSelectedRoles={(e: RoleListVm[]) => {
          setSelectedRoles(e);
        }}
      />
    ),
    disableTableSettings: true,
    headerOptions: (
      <Space>
        {isEditing ? (
          <Space>
            <Button
              type="text"
              onClick={() => {
                if (!isSaveDisabled) {
                  confirm({
                    okText: t("common.confirmDeleteModal.yes"),
                    okType: "danger",
                    cancelText: t("common.confirmDeleteModal.no"),
                    title: t(
                      "rolesAndPermissionsSettings.usersAndRolesAssign.confirmCancelModal.title"
                    ),
                    onOk: async () => {
                      setEditing(false);
                      resetChangedValues();
                    },
                  });
                } else {
                  setEditing(false);
                }
              }}
            >
              {t("common.cancel")}
            </Button>

            <Button
              type="primary"
              loading={updateUserRoleRelations.loading}
              onClick={handleSaveRelations}
              disabled={isSaveDisabled}
            >
              {t("common.save")}
            </Button>
          </Space>
        ) : (
          <Button
            icon={<MaterialIcon path={mdiPencil} />}
            loading={updateUserRoleRelations.loading}
            onClick={() => setEditing(true)}
          >
            {t("common.edit")}
          </Button>
        )}
      </Space>
    ),
    resetStore: dispatch.usersAndRolesAssign.reset,
    resetListParams: () => {
      setSelectedRoles([]);
      dispatch.usersAndRolesAssign.setFilterRoleId(null);
      dispatch.usersAndRolesAssign.resetListParams();
    },
    loading: getUserRoleRelations.loading,
  });

  return (
    <CustomTable<SimpleUserForUserRoleRelationVm>
      {...customTableProps}
      scrollX={"max-content"}
      className={styles.wrapper}
      selectable={false}
      pageHasTabs
    />
  );
};

export default UsersAndRolesAssign;
